import { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';
import { buttonVariants as themeButtonVariants } from '@/config/theme';

const buttonVariants = cva(themeButtonVariants.solid.base, {
  variants: {
    variant: {
      solid: themeButtonVariants.solid.variants.primary,
      outline: themeButtonVariants.outline.variants.primary,
      ghost: themeButtonVariants.ghost.variants.primary,
      link: themeButtonVariants.link.variants.primary,
      rounded: 'rounded-full',
      gradient: 'relative border border-[#D0D5DD] rounded-lg bg-gradient-to-r from-[#FCFCFD] to-[#F9F9FB] flex items-center justify-center gap-2.5 hover:shadow-sm transition-shadow duration-200',
    },
    color: {
      primary: '',
      secondary: '',
      success: '',
      error: '',
    },
    size: {
      sm: 'h-8 px-4 text-sm',
      default: 'h-10 px-6 text-base',
      lg: 'h-12 px-8 text-lg',
      xl: 'h-14 px-10 text-xl',
    },
  },
  compoundVariants: [
    {
      variant: 'solid',
      color: 'primary',
      className: themeButtonVariants.solid.variants.primary,
    },
    {
      variant: 'solid',
      color: 'secondary',
      className: themeButtonVariants.solid.variants.secondary,
    },
    {
      variant: 'solid',
      color: 'success',
      className: themeButtonVariants.solid.variants.success,
    },
    {
      variant: 'solid',
      color: 'error',
      className: themeButtonVariants.solid.variants.error,
    },
    {
      variant: 'outline',
      color: 'primary',
      className: themeButtonVariants.outline.variants.primary,
    },
    {
      variant: 'outline',
      color: 'secondary',
      className: themeButtonVariants.outline.variants.secondary,
    },
    {
      variant: 'outline',
      color: 'success',
      className: themeButtonVariants.outline.variants.success,
    },
    {
      variant: 'outline',
      color: 'error',
      className: themeButtonVariants.outline.variants.error,
    },
    {
      variant: 'ghost',
      color: 'primary',
      className: themeButtonVariants.ghost.variants.primary,
    },
    {
      variant: 'ghost',
      color: 'secondary',
      className: themeButtonVariants.ghost.variants.secondary,
    },
    {
      variant: 'ghost',
      color: 'success',
      className: themeButtonVariants.ghost.variants.success,
    },
    {
      variant: 'ghost',
      color: 'error',
      className: themeButtonVariants.ghost.variants.error,
    },
    {
      variant: 'link',
      color: 'primary',
      className: themeButtonVariants.link.variants.primary,
    },
    {
      variant: 'link',
      color: 'secondary',
      className: themeButtonVariants.link.variants.secondary,
    },
    {
      variant: 'link',
      color: 'success',
      className: themeButtonVariants.link.variants.success,
    },
    {
      variant: 'link',
      color: 'error',
      className: themeButtonVariants.link.variants.error,
    },
    {
      variant: 'rounded',
      color: 'primary',
      className: `${themeButtonVariants.solid.variants.primary}`,
    },
    {
      variant: 'rounded',
      color: 'secondary',
      className: `${themeButtonVariants.solid.variants.secondary}`,
    },
    {
      variant: 'rounded',
      color: 'success',
      className: `${themeButtonVariants.solid.variants.success}`,
    },
    {
      variant: 'rounded',
      color: 'error',
      className: `${themeButtonVariants.solid.variants.error}`,
    },
    {
      variant: 'gradient',
      color: 'primary',
      className: 'text-[#010812]',
    },
    {
      variant: 'gradient',
      color: 'secondary',
      className: 'text-[#343451]',
    },
    {
      variant: 'gradient',
      color: 'success',
      className: 'text-[#027A48]',
    },
    {
      variant: 'gradient',
      color: 'error',
      className: 'text-[#B42318]',
    },
  ],
  defaultVariants: {
    variant: 'solid',
    color: 'primary',
    size: 'default',
  },
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonVariants>,
    ButtonVariants {
  children: ReactNode;
}

export const Button = ({
  className,
  variant,
  color,
  size,
  children,
  ...props
}: ButtonProps) => {
  return (
    <button
      className={cn(buttonVariants({ variant, color, size, className }))}
      {...props}
    >
      {children}
    </button>
  );
};